import { Input, Textarea, Avatar } from '@lib/ui';
import { PersonaFieldHelp } from '../../components';
import type { WizardPersona } from '../../types';

export type IdentityStepProps = {
  persona: Partial<WizardPersona>;
  onChange: (updates: Partial<WizardPersona>) => void;
};

export function IdentityStep({ persona, onChange }: IdentityStepProps) {
  const updateWorldview = (updates: Partial<WizardPersona['worldview']>) => {
    onChange({
      worldview: {
        ...persona.worldview,
        coreBeliefs: persona.worldview?.coreBeliefs || [],
        values: persona.worldview?.values || [],
        audience: persona.worldview?.audience || '',
        purpose: persona.worldview?.purpose || '',
        ...updates,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Identity</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-dark-400">
          Define who this persona is and why it exists
        </p>
      </div>

      {/* Avatar preview */}
      <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-dark-700 rounded-lg">
        <Avatar
          size="xl"
          className="bg-primary-500 text-white text-xl font-semibold"
          name={persona.avatar || persona.name?.slice(0, 2).toUpperCase() || '??'}
        />
        <div className="flex-1">
          <p className="font-medium text-gray-900 dark:text-white">
            {persona.name || 'Persona Name'}
          </p>
          <p className="text-sm text-gray-500 dark:text-dark-400">
            {persona.tagline || 'Your tagline will appear here'}
          </p>
        </div>
      </div>

      {/* Name field */}
      <PersonaFieldHelp
        tooltip="This is the name you'll use to reference this persona in your workflow"
        example="Thought Leader, Tech Educator, Brand Voice"
        suggestions={['Thought Leader', 'Educator', 'Brand Voice', 'Expert']}
        onSuggestionClick={(suggestion) => onChange({ name: suggestion })}
      >
        <Input
          label="Persona Name"
          placeholder="e.g., Thought Leader, Tech Expert"
          value={persona.name || ''}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </PersonaFieldHelp>

      {/* Avatar initials */}
      <PersonaFieldHelp
        tooltip="A short abbreviation (1-2 characters) for visual identification"
        example="TL, TE, BV"
      >
        <Input
          label="Avatar Initials"
          placeholder="e.g., TL"
          maxLength={2}
          value={persona.avatar || ''}
          onChange={(e) => onChange({ avatar: e.target.value.toUpperCase() })}
          className="w-24"
        />
      </PersonaFieldHelp>

      {/* Tagline */}
      <PersonaFieldHelp
        tooltip="A brief statement that captures what this persona represents"
        example="Shaping the future of content creation"
        suggestions={[
          'Shaping the future of [industry]',
          'Making [topic] accessible',
          'Building communities around [passion]',
        ]}
        onSuggestionClick={(suggestion) => onChange({ tagline: suggestion })}
      >
        <Textarea
          label="Tagline"
          placeholder="e.g., Shaping the future of content creation"
          value={persona.tagline || ''}
          onChange={(e) => onChange({ tagline: e.target.value })}
          rows={2}
        />
      </PersonaFieldHelp>

      {/* Divider */}
      <div className="border-t border-gray-200 dark:border-dark-600 pt-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Worldview</h3>
        <p className="text-sm text-gray-500 dark:text-dark-400 mb-4">
          Who does this persona speak to and why?
        </p>
      </div>

      {/* Audience */}
      <PersonaFieldHelp
        tooltip="Describe the primary audience this persona is designed to reach"
        example="Health-curious women aged 20-35, Tech professionals seeking career growth"
        suggestions={[
          'Industry professionals and decision-makers',
          'Learners at all levels',
          'Community members seeking connection',
        ]}
        onSuggestionClick={(suggestion) => updateWorldview({ audience: suggestion })}
      >
        <Textarea
          label="Audience"
          placeholder="Who does this persona speak to?"
          value={persona.worldview?.audience || ''}
          onChange={(e) => updateWorldview({ audience: e.target.value })}
          rows={2}
        />
      </PersonaFieldHelp>

      {/* Purpose */}
      <PersonaFieldHelp
        tooltip="Why does this persona exist? What is its mission?"
        example="To share genuine wellness discoveries like advice from a trusted friend"
        suggestions={[
          'To challenge conventional thinking and illuminate new paths',
          'To break down barriers to understanding',
          'To foster genuine connections and celebrate collective wins',
        ]}
        onSuggestionClick={(suggestion) => updateWorldview({ purpose: suggestion })}
      >
        <Textarea
          label="Purpose"
          placeholder="Why does this persona exist?"
          value={persona.worldview?.purpose || ''}
          onChange={(e) => updateWorldview({ purpose: e.target.value })}
          rows={2}
        />
      </PersonaFieldHelp>
    </div>
  );
}
